import type { Ref } from 'vue'

import { ref, onUnmounted, watch } from 'vue'

/**
 * A Vue composable that uses the ResizeObserver API to observe changes to the
 * size of an element.
 * @param containerRef - A ref to the element to observe.
 * @param options - The ResizeObserverOptions to use.
 * @returns An object containing the entries observed by the ResizeObserver.
 */
export default function useResizeObserver(
  containerRef: Ref<HTMLElement | null>,
  options?: ResizeObserverOptions
): {
  entries: Ref<ResizeObserverEntry[]>
} {
  const entries = ref<ResizeObserverEntry[]>([])

  let observer: ResizeObserver | null = null

  const collectEntries = (mutationsList: ResizeObserverEntry[]): void => {
    entries.value = mutationsList
  }

  onUnmounted(() => {
    if (observer) {
      observer.disconnect()
    }
  })

  watch(containerRef, (newValue) => {
    if (newValue) {
      if (observer) {
        observer.disconnect()
      }

      observer = new ResizeObserver(collectEntries)
      observer.observe(newValue, options)
    }
  })

  return {
    entries,
  }
}
